import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ToastService } from '../../../../core/services/toast.service';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { ServiceApiModel, ServiceType, ServiceUnitType } from '../../models/service.models';
import { ServicesService } from '../../services/services.service';

interface UnitTypeOption {
  value: ServiceUnitType;
  label: string;
}

interface ServiceTypeOption {
  value: ServiceType;
  label: string;
}

@Component({
  selector: 'app-services',
  imports: [CommonModule, ReactiveFormsModule, BreadcrumbsComponent],
  templateUrl: './services.component.html',
  styleUrl: './services.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicesComponent {
  private readonly fb = inject(FormBuilder);
  private readonly servicesService = inject(ServicesService);
  private readonly toastService = inject(ToastService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly services = signal<ServiceApiModel[]>([]);
  readonly servicePendingDisable = signal<ServiceApiModel | null>(null);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', route: '/home' },
    { label: 'Services' },
  ];

  readonly unitTypeOptions: UnitTypeOption[] = [
    { value: 'days', label: '# of days' },
    { value: 'amount', label: '$ amount' },
    { value: 'events', label: '# of events' },
    { value: 'appointments', label: '# of appointments' },
  ];

  readonly serviceTypeOptions: ServiceTypeOption[] = [
    { value: 'behavioral_health', label: 'Behavioral Health' },
    { value: 'employment', label: 'Employment' },
    { value: 'education', label: 'Education' },
    { value: 'housing', label: 'Housing' },
    { value: 'mediation', label: 'Mediation' },
    { value: 'retaliation_prevention', label: 'Retaliation Prevention' },
    { value: 'crisis_intervention', label: 'Crisis Intervention' },
    { value: 'other', label: 'Other' },
  ];

  private readonly serviceTypeLabels: Record<ServiceType, string> = {
    behavioral_health: 'Behavioral Health',
    employment: 'Employment',
    education: 'Education',
    housing: 'Housing',
    mediation: 'Mediation',
    retaliation_prevention: 'Retaliation Prevention',
    crisis_intervention: 'Crisis Intervention',
    other: 'Other',
  };

  readonly serviceForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: ['', [Validators.maxLength(2000)]],
    serviceType: ['other' as ServiceType, [Validators.required]],
    serviceTypeOther: ['', [Validators.maxLength(255)]],
    unitType: ['events' as ServiceUnitType, [Validators.required]],
  });

  readonly activeServices = computed(() => this.services());
  readonly isDisableDialogOpen = computed(() => this.servicePendingDisable() !== null);

  constructor() {
    this.serviceForm.controls.serviceType.valueChanges.subscribe((serviceType) => {
      const otherControl = this.serviceForm.controls.serviceTypeOther;
      if (serviceType === 'other') {
        otherControl.addValidators([Validators.required]);
      } else {
        otherControl.removeValidators([Validators.required]);
        otherControl.setValue('');
      }
      otherControl.updateValueAndValidity({ emitEvent: false });
    });

    this.loadServices();
  }

  startCreate(): void {
    this.serviceForm.reset({
      name: '',
      description: '',
      serviceType: 'other',
      serviceTypeOther: '',
      unitType: 'events',
    });
  }

  submit(): void {
    if (this.serviceForm.invalid || this.saving()) {
      this.serviceForm.markAllAsTouched();
      return;
    }

    const raw = this.serviceForm.getRawValue();
    const payload = {
      name: raw.name.trim(),
      description: raw.description.trim(),
      serviceType: raw.serviceType,
      serviceTypeOther: raw.serviceTypeOther.trim(),
      unitType: raw.unitType,
    };

    if (!payload.name) {
      this.toastService.warning('Invalid Service Name', 'Service name is required.');
      return;
    }

    if (payload.serviceType === 'other' && !payload.serviceTypeOther) {
      this.toastService.warning('Invalid Service Type', 'Service Type Other is required when Service Type is Other.');
      return;
    }

    const servicePayload = {
      ...payload,
      serviceTypeOther: payload.serviceType === 'other' ? payload.serviceTypeOther : null,
    };

    this.saving.set(true);

    this.servicesService.createService(servicePayload).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.toastService.success('Service Created', 'Service created successfully.');
        this.startCreate();
        this.loadServices();
      },
      error: (error) => {
        this.toastService.danger(
          'Create Failed',
          error?.error?.message ?? 'Unable to save service right now.',
        );
      },
    });
  }

  requestDisableService(service: ServiceApiModel): void {
    if (this.saving()) {
      return;
    }

    this.servicePendingDisable.set(service);
  }

  confirmDisableService(): void {
    const service = this.servicePendingDisable();
    if (!service || this.saving()) {
      return;
    }

    this.servicePendingDisable.set(null);
    this.disableService(service);
  }

  cancelDisableService(): void {
    this.servicePendingDisable.set(null);
  }

  getServiceTypeLabel(service: ServiceApiModel): string {
    if (service.serviceType === 'other') {
      return service.serviceTypeOther?.trim() || this.serviceTypeLabels.other;
    }

    return this.serviceTypeLabels[service.serviceType];
  }

  private disableService(service: ServiceApiModel): void {
    if (this.saving()) {
      return;
    }

    this.saving.set(true);

    this.servicesService
      .disableService(service.id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.toastService.success(
            'Service Disabled',
            'Service is now inactive and no longer selectable for new case work.',
          );
          this.loadServices();
        },
        error: (error) => {
          this.toastService.danger(
            'Service Disable Failed',
            error?.error?.message ?? 'Unable to disable service right now.',
          );
        },
      });
  }

  private loadServices(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.servicesService
      .listServices()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.services.set(data);
        },
        error: (error) => {
          this.errorMessage.set(error?.error?.message ?? 'Unable to load services right now.');
        },
      });
  }
}
